/**
 * useChatMessages — 聊天消息管理 Hook
 *
 * 职责：
 * - 从后端加载指定会话的消息列表
 * - 发送消息（非流式 / 流式通过 useChatStream 代理）
 * - 管理本地消息列表状态
 * - 追踪 AI 回复的 loading 状态
 *
 * 模块协作：
 *   useChatMessages 内部调用 useChatStream 处理 SSE 网络层，
 *   useChatMessages 负责消息状态管理，useChatStream 负责流式数据传输。
 */

import { useState, useCallback, useRef } from 'react';
import { chatAPI } from '@/service/chat';
import type { ChatMessageRecord, SSEEventChunk } from '@/service/chat';
import type { ChatMessage } from '@/types';
import { useChatStream } from './useChatStream';
import { toChatMessage } from '../types';

/** 消息发送状态 */
export type SendStatus = 'idle' | 'sending' | 'streaming' | 'completed' | 'error';

/** useChatMessages 返回值类型 */
export interface UseChatMessagesReturn {
  /** 当前会话的消息列表 */
  messages: ChatMessage[];
  /** 消息发送状态 */
  sendStatus: SendStatus;
  /** 错误信息 */
  error: string | null;
  /** 加载消息列表 */
  loadMessages: (sessionId: number) => Promise<void>;
  /**
   * 发送消息（非流式）
   * @param sessionId 会话 ID
   * @param content 消息内容
   * @returns 返回 AI 回复的 messageId，失败返回 null
   */
  sendMessage: (sessionId: number, content: string) => Promise<string | null>;
  /**
   * 发送消息并消费 SSE 流式响应
   * 内部委托给 useChatStream 处理网络层
   * @param sessionId 会话 ID
   * @param content 消息内容
   * @param onChunk 每个 SSE 事件块的回调
   * @param onTempMessageId AI 临时消息 ID 回调（用于外部追踪 thinking 状态）
   * @param signal AbortSignal 用于中断
   * @returns AI 回复的 messageId，失败返回 null
   */
  sendMessageStream: (
    sessionId: number,
    content: string,
    onChunk: (chunk: { content?: string; thinking?: string }) => void,
    onTempMessageId?: (tempId: string) => void,
    signal?: AbortSignal
  ) => Promise<string | null>;
  /** 重置消息列表（切换会话时） */
  resetMessages: () => void;
}

/**
 * 管理聊天消息的 Hook
 *
 * 通过组合 useChatStream 实现流式响应的网络层处理，
 * 自身专注于消息列表状态管理和 UI 状态追踪。
 */
export const useChatMessages = (): UseChatMessagesReturn => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [sendStatus, setSendStatus] = useState<SendStatus>('idle');
  const [error, setError] = useState<string | null>(null);

  // 临时 AI 消息 ID 引用，供流式回调中更新消息使用
  const tempAiIdRef = useRef<string | null>(null);

  // ==================== 流式处理（通过 useChatStream 代理） ====================

  const { startStream, abortStream } = useChatStream({
    /**
     * 每收到一个 SSE 事件块时的回调
     * 根据事件类型更新对应的消息状态
     */
    onChunk: (chunk: SSEEventChunk) => {
      const tempId = tempAiIdRef.current;
      if (!tempId) return;
      
      if (chunk.type === 'content' && chunk.content) {
        // content 事件：逐步累积回复内容，标记为 streaming
        setMessages((prev) =>
          prev.map((m) =>
            m.id === tempId
              ? { ...m, content: (m.content || '') + chunk.content!, status: 'streaming' as const }
              : m
          )
        );
      } else if (chunk.type === 'thinking' && chunk.thinking) {
        // thinking 事件：逐步累积思考过程
        setMessages((prev) =>
          prev.map((m) =>
            m.id === tempId
              ? { ...m, thinking: (m.thinking || '') + chunk.thinking! }
              : m
          )
        );
      } else if (chunk.type === 'done' && chunk.content) {
        // done 事件：服务端返回完整内容，替换累积内容
        setMessages((prev) =>
          prev.map((m) =>
            m.id === tempId
              ? { ...m, content: chunk.content!, status: 'completed' as const }
              : m
          )
        );
      } else if (chunk.type === 'error') {
        setError(chunk.error || 'AI 响应错误');
        setSendStatus('error');
      }
    },

    /**
     * 流完成回调
     * 最终更新消息状态为 completed（处理 final 事件）
     */
    onComplete: () => {
      const tempId = tempAiIdRef.current;
      if (tempId) {
        setMessages((prev) =>
          prev.map((m) =>
            m.id === tempId && m.status !== 'completed'
              ? { ...m, status: 'completed' as const }
              : m
          )
        );
      }
      setSendStatus('completed');
      tempAiIdRef.current = null;
    },

    /**
     * 流错误回调
     */
    onError: (errMsg: string) => {
      setError(errMsg);
      setSendStatus('error');
      tempAiIdRef.current = null;
    },
  });

  // ==================== 消息列表加载 ====================

  /**
   * 从后端加载指定会话的消息列表
   * @param sessionId 会话 ID
   */
  const loadMessages = useCallback(async (sessionId: number) => {
    setError(null);
    try {
      const res = await chatAPI.getMessages(sessionId);
      if (res.success) {
        // 后端消息按 sort_order ASC 排序，直接转换
        const chatMessages = res.data.list.map(toChatMessage);
        setMessages(chatMessages);
      } else {
        setError(res.message || '获取消息列表失败');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : '获取消息列表失败');
    }
  }, []);

  // ==================== 发送消息（非流式） ====================

  /**
   * 发送消息（非流式）
   * 通过 POST /api/sessions/:sessionId/messages 一次性获取完整回复
   */
  const sendMessage = useCallback(
    async (sessionId: number, content: string): Promise<string | null> => {
      setSendStatus('sending');
      setError(null);

      // 先添加用户消息到本地列表
      const tempUserMsg: ChatMessage = {
        id: `temp-user-${Date.now()}`,
        role: 'user',
        content,
        timestamp: Date.now(),
        status: 'completed',
      };
      setMessages((prev) => [...prev, tempUserMsg]);

      try {
        const res = await chatAPI.sendMessage(sessionId, content);
        if (res.success) {
          const data = res.data;

          if (data.aiMessage) {
            const aiMsg: ChatMessageRecord = data.aiMessage;
            setMessages((prev) => [...prev, toChatMessage(aiMsg)]);
            const aiId = String(aiMsg.id);
            setSendStatus('completed');
            return aiId;
          } else {
            if (data.error) setError(data.error);
            setSendStatus('error');
            return null;
          }
        } else {
          setError(res.message || '发送消息失败');
          setSendStatus('error');
          return null;
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : '发送消息失败');
        setSendStatus('error');
        return null;
      }
    },
    []
  );

  // ==================== 发送消息（流式） ====================

  /**
   * 发送消息并消费 SSE 流式响应
   *
   * 内部流程：
   * 1. 创建用户消息和 AI 临时消息，加入列表
   * 2. 调用 useChatStream.startStream() 发起 SSE 请求
   * 3. useChatStream 的 onChunk 回调自动更新消息内容
   *
   * @param sessionId 会话 ID
   * @param content 用户消息内容
   * @param onChunk 外部回调（用于外部 UI 更新）
   * @param onTempMessageId AI 临时消息 ID 回调
   * @param signal 中断信号
   * @returns AI 消息 ID（临时），失败返回 null
   */
  const sendMessageStream = useCallback(
    async (
      sessionId: number,
      content: string,
      onChunk: (chunk: { content?: string; thinking?: string }) => void,
      onTempMessageId?: (tempId: string) => void,
      signal?: AbortSignal
    ): Promise<string | null> => {
      setSendStatus('sending');
      setError(null);

      // 1. 添加用户消息
      const tempUserMsg: ChatMessage = {
        id: `temp-user-${Date.now()}`,
        role: 'user',
        content,
        timestamp: Date.now(),
        status: 'completed',
      };
      setMessages((prev) => [...prev, tempUserMsg]);

      // 2. 添加 AI 消息占位（thinking 状态），通知外部
      const tempAiId = `temp-ai-${Date.now()}`;
      tempAiIdRef.current = tempAiId;
      const tempAiMsg: ChatMessage = {
        id: tempAiId,
        role: 'assistant',
        content: '',
        timestamp: Date.now(),
        status: 'thinking',
      };
      setMessages((prev) => [...prev, tempAiMsg]);
      onTempMessageId?.(tempAiId);

      // 3. 委托 useChatStream 发起网络请求
      // 注意：如果 signal 已中止，startStream 内部的 abort 机制会处理
      await startStream(sessionId, content, signal);

      // 4. 如果被外部中止，清理状态
      if (signal?.aborted) {
        resetStreamState();
      }

      return tempAiId;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [startStream]
  );

  /** 重置流式相关状态 */
  const resetStreamState = () => {
    tempAiIdRef.current = null;
    if (sendStatus === 'sending' || sendStatus === 'streaming') {
      setSendStatus('idle');
    }
  };

  // ==================== 重置 ====================

  /**
   * 重置消息列表（切换会话时调用）
   */
  const resetMessages = useCallback(() => {
    abortStream();
    setMessages([]);
    setSendStatus('idle');
    setError(null);
    tempAiIdRef.current = null;
  }, [abortStream]);

  return {
    messages,
    sendStatus,
    error,
    loadMessages,
    sendMessage,
    sendMessageStream,
    resetMessages,
  };
};
